import { useRef, useState } from "react";
import { FileUp, FileText, Loader2, Trash2, Eye, EyeOff, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InfoNote } from "@/components/common/Cards";

const MAX = 20;

export function UploadDialog({ open, onOpenChange, onDone }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState({});
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const files = [...(list || [])].filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (!files.length) {
      toast.error("Pilih file PDF tagihan.");
      return;
    }
    setRows((r) => [...r, ...files.map((f) => ({ key: `${f.name}-${f.size}-${Math.random()}`, file: f, password: "", label: "" }))].slice(0, MAX));
  };
  const setRow = (key, patch) => setRows((r) => r.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const close = (v) => {
    if (busy) return;
    if (!v) setRows([]);
    onOpenChange(v);
  };

  const submit = async () => {
    if (!rows.length) return;
    setBusy(true);
    const fd = new FormData();
    rows.forEach((r) => fd.append("files", r.file));
    fd.append("meta", JSON.stringify(rows.map((r) => ({ password: r.password, label: r.label }))));
    try {
      const { data } = await api.post("/cc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = data.results || [];
      const ok = res.filter((x) => x.status === "processing").length;
      const dup = res.filter((x) => x.duplicate).length;
      const bad = res.filter((x) => x.status === "error");
      if (ok) toast.success(`${ok} tagihan diunggah, AI sedang merangkum…`);
      if (dup) toast.info(`${dup} file sudah pernah diimpor, dilewati.`);
      if (bad.length) toast.warning(`${bad.length} file perlu tindakan: ${bad[0].error}`);
      setRows([]);
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengunggah"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl bg-card border-border" data-testid="cc-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Upload PDF tagihan</DialogTitle>
          <DialogDescription>
            Unggah beberapa e-statement sekaligus (maks. {MAX}). Isi password PDF per file — kosongkan bila PDF tidak terkunci atau password sudah tersimpan di Pengaturan Gmail.
          </DialogDescription>
        </DialogHeader>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            addFiles(e.dataTransfer.files);
          }}
          className={`w-full rounded-xl border border-dashed p-6 flex flex-col items-center gap-2 transition-colors ${
            drag ? "border-primary bg-[hsl(43_30%_8%)]" : "border-[hsl(220_10%_24%)] bg-[hsl(220_12%_7%)] hover:border-primary/60"
          }`}
          data-testid="cc-upload-dropzone"
        >
          <UploadCloud className="h-7 w-7 text-primary" />
          <span className="text-sm">Tarik & lepas PDF di sini, atau klik untuk memilih</span>
          <span className="text-[11px] text-muted-foreground">PDF asli dari bank, maks. 15 MB per file</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid="cc-upload-input"
        />

        {rows.length > 0 && (
          <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1" data-testid="cc-upload-rows">
            {rows.map((r, i) => (
              <div key={r.key} className="rounded-lg border border-border bg-[hsl(220_12%_8%)] p-3" data-testid={`cc-upload-row-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm truncate flex-1">{r.file.name}</span>
                  <span className="text-[11px] text-muted-foreground num">{(r.file.size / 1024).toFixed(0)} KB</span>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRows((x) => x.filter((y) => y.key !== r.key))} data-testid={`cc-upload-remove-${i}`} aria-label="Hapus file">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Password PDF</Label>
                    <div className="relative">
                      <Input
                        type={show[r.key] ? "text" : "password"}
                        value={r.password}
                        onChange={(e) => setRow(r.key, { password: e.target.value })}
                        placeholder="mis. tanggal lahir DDMMYY"
                        className="h-9 pr-9 bg-[hsl(220_12%_6%)]"
                        autoComplete="off"
                        data-testid={`cc-upload-password-${i}`}
                      />
                      <button type="button" onClick={() => setShow((s) => ({ ...s, [r.key]: !s[r.key] }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary" aria-label="Tampilkan password">
                        {show[r.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Nama kartu (opsional)</Label>
                    <Input value={r.label} onChange={(e) => setRow(r.key, { label: e.target.value })} placeholder="Otomatis dari AI" className="h-9 bg-[hsl(220_12%_6%)]" data-testid={`cc-upload-label-${i}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <InfoNote>Password hanya dipakai untuk membuka PDF dan disimpan terenkripsi. File asli tetap tersimpan agar bisa dianalisa ulang.</InfoNote>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)} disabled={busy} data-testid="cc-upload-cancel">Batal</Button>
          <Button onClick={submit} disabled={!rows.length || busy} className="press" data-testid="cc-upload-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}
            Proses {rows.length || ""} PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
